/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import Render from '@ion/dls/src/render/Render';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import {LabelTypography} from '@ion/dls/src/typography/LabelTypography';
import {REPORT_CARD_WIDTH} from './types';
import {translateFolderOrDashboard} from './utils';

const useStyles = makeStyles((theme) => ({
  '@keyframes bounceZoom' : {
    '0%'   : {transform: 'scale(1)'},
    '30%'  : {transform: 'scale(1.05)'},
    '60%'  : {transform: 'scale(0.98)'},
    '100%' : {transform: 'scale(1.02)'}
  },

  // Static (non theme-toggled) so JSS attaches a single shared rule for every
  // card instance - keeping this inside the toggleThemeStyle()-wrapped
  // `reportCard` value below made it a per-instance dynamic rule, and the
  // local `$bounceZoom` keyframe reference only resolved for the first
  // mounted card, silently breaking hover on every card after it.
  reportCardHover : {
    '&:hover' : {
      zIndex    : dlsZIndex('dropdownPlaceholder'),
      animation : '$bounceZoom 0.6s ease',
      boxShadow : '0 4px 12px rgba(0, 0, 0, 0.15)',
      transform : 'scale(1.02)'
    }
  },

  reportCard : toggleThemeStyle({
    border          : '1px solid #ccc',
    borderRadius    : '4px',
    padding         : '18px 16px 0px 12px',
    backgroundColor : dlsColor('white'),
    width           : REPORT_CARD_WIDTH,
    height          : '120px',
    textAlign       : 'start',
    cursor          : 'pointer',
    boxSizing       : 'border-box',
    transition      : 'transform 0.3s ease'
  }, {
    gap             : '16px',
    width           : '327px',
    border          : `1px solid ${dlsColor('grey5')}`,
    cursor          : 'pointer',
    height          : '168px',
    display         : 'flex',
    padding         : '24px',
    position        : 'relative',
    boxSizing       : 'border-box',
    textAlign       : 'start',
    transition      : 'transform 0.3s ease',
    borderRadius    : '12px',
    flexDirection   : 'column',
    backgroundColor : dlsColor('white')
  }),

  accentBar : {
    position     : 'absolute',
    left         : 0,
    top          : 0,
    width        : '8px',
    height       : '100%',
    borderRadius : '100px 0 0 100px'
  },

  headerGroup : {
    display       : 'flex',
    flexDirection : 'column',
    gap           : '8px'
  },

  title : toggleThemeStyle({
    width           : '100%',
    alignContent    : 'center',
    display         : '-webkit-box',
    WebkitLineClamp : 2,
    WebkitBoxOrient : 'vertical',
    overflow        : 'hidden',
    textOverflow    : 'ellipsis'
  }, {
    color           : dlsColor('grey2'),
    width           : '100%',
    display         : '-webkit-box',
    fontSize        : '20px',
    overflow        : 'hidden',
    fontFamily      : 'Inter',
    fontWeight      : 700,
    lineHeight      : '18px',
    alignContent    : 'center',
    textOverflow    : 'ellipsis',
    WebkitBoxOrient : 'vertical',
    WebkitLineClamp : 2
  }),

  category : toggleThemeStyle({
    lineHeight : '24px',
    padding    : '4px 0 !important'
  }, {
    color         : `${dlsColor('grey3')} !important`,
    padding       : '4px 0 !important',
    fontSize      : '10px !important',
    fontFamily    : 'Roboto !important',
    fontWeight    : '700 !important' as any,
    lineHeight    : '12px !important',
    letterSpacing : '1px !important',
    textTransform : 'uppercase'
  }),

  description : {
    color      : dlsColor('grey3'),
    fontSize   : '12px',
    fontFamily : 'Inter',
    fontWeight : 500,
    lineHeight : '16px'
  }
}) as any);

/**
 * Props for the ReportCard component.
 */
interface ReportCardProps {
  dashboard: any;
  setSelected: (dashboard: any) => void;
  toggleCategory: (categoryName: string, value?: boolean) => void;
  setSideTab: Function;
  description?: string;
};

/**
 * ReportCard displays individual dashboard info including title and category.
 * It highlights the dashboard's folder color on the left and allows the user to
 * select the dashboard and navigate to its category.
 *
 * ```typescript
 * <ReportCard dashboard={dashboardItem}
 *   setSelected={(db) => setDashboard(db)}
 *   toggleCategory={(category) => console.log(category)} />
 * ```
 */
const ReportCard = ({
  dashboard,
  setSideTab,
  description,
  setSelected,
  toggleCategory
}: ReportCardProps) => {
  const classes = useStyles();

  const {
    isLegacy  : isLegacyTheme,
    isCurrent : isCurrentTheme
  } = useThemeContext();

  /**
   * Handles click event to select dashboard and toggle category.
   */
  const onClick = useCallback(() => {
    setSelected(dashboard);
    setSideTab(dashboard?.tab);
    const t = setTimeout(() => {
      toggleCategory(dashboard.category?.name || translate('sisense:analytics.folderNames.myReports'), true);
      clearTimeout(t);
    }, 400);
  }, [dashboard]);

  return (
    <div style={{borderLeft: `4px solid ${dashboard.color}`}}
      className={classnames(classes.reportCard, classes.reportCardHover)}
      onClick={onClick}>
      <Render.If condition={isLegacyTheme}>
        <div className={classes.title}>
          <HeaderTypography variant='h4'>
            {translateFolderOrDashboard(dashboard.oid, dashboard.title, 'dashboard')}
          </HeaderTypography>
        </div>
      </Render.If>
      <Render.If condition={isCurrentTheme}>
        <div className={classes.accentBar}
          style={{backgroundColor: dashboard.color}} />
      </Render.If>
      <div className={isCurrentTheme ? classes.headerGroup : ''}>
        <LabelTypography variantType='label3' className={classes.category}>
          {translateFolderOrDashboard(dashboard.category?.oid, dashboard.category?.name, 'folder')}
        </LabelTypography>
        <Render.If condition={isCurrentTheme}>
          <div className={classes.title}>
            {translateFolderOrDashboard(dashboard.oid, dashboard.title, 'dashboard')}
          </div>
        </Render.If>
      </div>
      <Render.If condition={isCurrentTheme && description}>
        <div className={classes.description}>
          {description}
        </div>
      </Render.If>
    </div>
  );
};

export default memo(ReportCard);
